import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon } from 'lucide-react';
import { apiClient, SystemEmbeddingResponse } from '../lib/api';
import SystemEmbeddingModal from '../components/SystemEmbeddingModal';
import ConfirmationModal from '../components/ConfirmationModal';

export default function SystemEmbeddings() {
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedEmbedding, setSelectedEmbedding] = useState<SystemEmbeddingResponse | null>(null);
    const [error, setError] = useState('');

    const queryClient = useQueryClient();

    // Fetch embeddings
    const { data: embeddings = [], isLoading, error: fetchError } = useQuery({
        queryKey: ['system-embeddings'],
        queryFn: apiClient.getSystemEmbeddings,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retry: false,
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: apiClient.deleteSystemEmbedding,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['system-embeddings'] });
            setDeleteModalOpen(false);
            setSelectedEmbedding(null);
        },
        onError: (error: Error) => {
            setError(error.message);
        },
    });

    const handleCreate = () => {
        setSelectedEmbedding(null);
        setModalOpen(true);
    };

    const handleEdit = (embedding: SystemEmbeddingResponse) => {
        setSelectedEmbedding(embedding);
        setModalOpen(true);
    };

    const handleDelete = (embedding: SystemEmbeddingResponse) => {
        setSelectedEmbedding(embedding);
        setDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        if (selectedEmbedding) {
            deleteMutation.mutate(selectedEmbedding.id);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-lg text-gray-600">Loading system embeddings...</div>
            </div>
        );
    }

    return (
        <div>
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
                        System Embeddings
                    </h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Manage global embeddings used for system-wide commands, FAQs, and common responses.
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
                    <button
                        type="button"
                        onClick={handleCreate}
                        className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:w-auto"
                    >
                        <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                        New Embedding
                    </button>
                </div>
            </div>

            {(fetchError || error) && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {(error || (fetchError as Error)?.message) || 'An error occurred'}
                </div>
            )}

            <div className="mt-8 flex flex-col">
                <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                            <table className="min-w-full divide-y divide-gray-300">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Type
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Content Preview
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Last Updated
                                        </th>
                                        <th scope="col" className="relative px-6 py-3">
                                            <span className="sr-only">Actions</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {embeddings.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                                                No system embeddings found.
                                            </td>
                                        </tr>
                                    ) : (
                                        embeddings.map((embedding) => (
                                            <tr key={embedding.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {embedding.name}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 uppercase">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${embedding.command_type === 'faq' ? 'bg-green-100 text-green-800' :
                                                        embedding.command_type === 'product' ? 'bg-blue-100 text-blue-800' :
                                                            'bg-purple-100 text-purple-800'
                                                        }`}>
                                                        {embedding.command_type}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                                                    {embedding.text_content}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {new Date(embedding.updated_at || embedding.created_at).toLocaleDateString()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <button
                                                        onClick={() => handleEdit(embedding)}
                                                        className="text-primary-600 hover:text-primary-900 mr-4"
                                                    >
                                                        <PencilIcon className="h-5 w-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(embedding)}
                                                        className="text-red-600 hover:text-red-900"
                                                    >
                                                        <TrashIcon className="h-5 w-5" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <SystemEmbeddingModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                embedding={selectedEmbedding}
            />

            <ConfirmationModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete System Embedding"
                message={`Are you sure you want to delete "${selectedEmbedding?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                type="danger"
                isLoading={deleteMutation.isLoading}
            />
        </div>
    );
}